import { z } from 'zod';
import { MoneySchema, ShortIdSchema, TimestampSchema, UidSchema } from './primitives.js';
import { TENANT_PRIVATE_COLLECTION } from './tenant.js';

/**
 * Sprint 98 — Operator wallet (collected wash payments, pending payout).
 *
 * Kilat collects customer wash payments centrally via toyyibPay under
 * Kilat Sdn Bhd's own merchant account, then disburses to operators via
 * the Sprint 97 DuitNow CSV pipeline. Each tenant has:
 *
 *   tenants/{tid}/walletLedger/{entryId}  ← canonical balance source
 *   tenants/{tid}/private/payoutBank      ← operator's destination + threshold
 *
 * Why a ledger, not a balance counter: counters drift; ledgers
 * reconcile. Same reason `agents/{aid}/earningsLedger` is the truth
 * for agent commissions (Sprint 97). PSP webhook hiccups can be
 * replayed against the ledger to detect double-credits.
 *
 * Kilat takes 0% margin per [[project-zero-cut-principle]]. The
 * `psp_fee` entries are recorded against the wash so reconciliation
 * stays clean, but the customer absorbs that fee at checkout as a
 * visible line item — operator receives the full wash price.
 */

// ── Ledger entry ────────────────────────────────────────────────────────

export const TenantWalletEntryKindSchema = z.enum([
  'collected', // customer paid; amount positive
  'psp_fee', // toyyibPay fee for THAT collection; recorded for reconciliation
  //   (customer pays it visibly at checkout; amount stored negative
  //   so the ledger sums to operator's net receivable)
  'refund', // wash refunded; amount negative
  'withdraw', // payout debited; amount negative; carries payoutId
  'adjustment', // super-admin manual entry; signed
]);
export type TenantWalletEntryKind = z.infer<typeof TenantWalletEntryKindSchema>;

export const TenantWalletLedgerEntrySchema = z.object({
  /**
   * NOT `ShortIdSchema` (max 24). Every server writer uses a deterministic
   * prefixed id — `collected-{intentId}`, `withdraw-{payoutId}`,
   * `refund-{refundId}` — and a Firestore auto-id is 20 characters, so those
   * ids run 27-30 characters. Under the old cap the dashboard's wallet repo
   * (which drops entries that fail to parse) silently dropped every one of
   * them from the operator's balance.
   */
  id: z.string().min(1).max(128),
  kind: TenantWalletEntryKindSchema,
  /** Sen, signed. Positive for collected/adjustment-credit, negative for
   *  psp_fee/refund/withdraw/adjustment-debit. */
  amount: z.number().int(),
  accruedAt: TimestampSchema,
  /** Populated when this entry has been consumed by a payout batch. */
  payoutId: z.string().optional(),
  /** Optional reference to the wash ticket that triggered this entry. The
   *  toyyibPay credit writes an explicit `null` for a wash-pack payment. */
  customerWashTicketId: z.string().nullish(),
  /** Optional reference to the toyyibPay billcode (or other PSP ref). */
  pspRef: z.string().nullish(),
  /** Optional reference to the wash-pack subscription this collection paid for.
   *  Written as `null` until the pack is issued. */
  subscriptionId: z.string().nullish(),
  /** Snapshot of context — e.g. {customerUid, washPackageName, paymentMethod}. */
  basis: z.record(z.string(), z.unknown()).default({}),
  /** Set on `adjustment` and `withdraw` entries — uid of who wrote it. */
  writtenBy: UidSchema.optional(),
  notes: z.string().max(500).optional(),
});
export type TenantWalletLedgerEntry = z.infer<typeof TenantWalletLedgerEntrySchema>;

// ── Bank account / payout destination ──────────────────────────────────

/**
 * Operator's bank or DuitNow destination. Stored as ONE document at
 * `tenants/{tid}/private/payoutBank` — one destination per tenant is the MVP
 * shape.
 *
 * WHERE THIS LIVES, AND WHY. It sat on the tenant root as
 * `tenants/{tid}.payoutBankAccount` until 2026-10-15. That document is
 * `allow read: if true` (the unauthenticated customer PWA boots from it), so an
 * operator's account number, account holder name and DuitNow ID — very often
 * an NRIC — were readable by anyone holding a tenant id, and tenant ids are
 * handed out by `slugToTenantId` and the public `outletDirectory`. Owner AND
 * manager could also write it straight from the client, and a manager
 * repointing the payout destination is the obvious fraud path.
 *
 * Now: read is owner/manager/brand super-admin (`match /private/{docId}`),
 * client write is denied, and the only writer is the owner-only
 * `setPayoutBankAccount` callable. `migrate-private-tenant-data.ts` moves any
 * legacy field across.
 *
 * `duitNowId` takes precedence over bank-account fields when both are
 * present — DuitNow proxy transfers settle T+0, bank-account transfers
 * settle T+1 in MY.
 */
export const TenantPayoutBankAccountSchema = z.object({
  bankName: z.string().max(80).optional(),
  accountName: z.string().max(100).optional(),
  accountNumber: z.string().max(40).optional(),
  /** NRIC, mobile number, or SSM ID for DuitNow proxy transfers. */
  duitNowId: z.string().max(40).optional(),
  /** Minimum balance (sen) before a payout will be initiated. Operator can
   *  manually request below threshold; auto-withdraw respects it. */
  thresholdSen: MoneySchema.default(10_000).describe('Min payout threshold; default RM 100'),
  /** Whether the daily auto-withdraw job sweeps this tenant. Defaults
   *  false — operators opt in to auto, so a fresh signup never gets a
   *  surprise withdrawal. */
  autoWithdrawEnabled: z.boolean().default(false),
  updatedAt: TimestampSchema.optional(),
  updatedBy: UidSchema.optional(),
});
export type TenantPayoutBankAccount = z.infer<typeof TenantPayoutBankAccountSchema>;

/** Doc id under `tenants/{tid}/private/` holding the payout destination. */
export const TENANT_PAYOUT_BANK_DOC_ID = 'payoutBank';

/** The one place that spells this path, so readers and the writer cannot drift. */
export function tenantPayoutBankPath(tenantId: string): string {
  return `tenants/${tenantId}/${TENANT_PRIVATE_COLLECTION}/${TENANT_PAYOUT_BANK_DOC_ID}`;
}

/**
 * The stored document at `tenants/{tid}/private/payoutBank`. `id` is optional
 * because repositories attach the doc id after parsing (ADR-0026).
 */
export const TenantPayoutBankDocSchema = TenantPayoutBankAccountSchema.extend({
  id: z.string().optional().describe('Doc id, attached by the repository after parse'),
});
export type TenantPayoutBankDoc = z.infer<typeof TenantPayoutBankDocSchema>;

// ── Tenant payout doc (Sprint 97 mirror) ────────────────────────────────

/**
 * `tenants/{tid}/payouts/{payoutId}` — one doc per super-admin-initiated
 * tenant payout batch. Mirrors `AgentPayoutSchema` / `RecruiterPayoutSchema`
 * from Sprint 97 so `runPayoutBatch` can treat all three recipient kinds
 * uniformly.
 */
export const TenantPayoutSchema = z.object({
  id: ShortIdSchema,
  periodStart: TimestampSchema,
  periodEnd: TimestampSchema,
  items: z.array(ShortIdSchema),
  grossAmount: MoneySchema,
  status: z.enum(['pending', 'processing', 'paid', 'failed']),
  method: z.enum(['duitnow', 'bank_transfer']),
  destination: TenantPayoutBankAccountSchema,
  initiatedAt: TimestampSchema,
  paidAt: TimestampSchema.optional(),
  txRef: z.string().optional(),
});
export type TenantPayout = z.infer<typeof TenantPayoutSchema>;
